"use client";

import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Trash2, Sparkles, RefreshCw, User, Square, AlertTriangle, RotateCw, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { UpgradePrompt } from "@/components/upgrade-prompt";
import { CoachTipsSheet } from "@/components/coach-tips-sheet";
import { BookmarksDrawer } from "@/components/bookmarks-drawer";
import { useChat } from "@/hooks/use-chat";
import type { SleepEvent, Child, SleepGoal } from "@/lib/types";

// Format timestamp in a user-friendly way
function formatMessageTimestamp(date: Date): string {
  const now = new Date();
  const messageDate = new Date(date);
  const diffMs = now.getTime() - messageDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Just now (less than 1 minute)
  if (diffMins < 1) {
    return "Just now";
  }

  // Minutes ago (less than 1 hour)
  if (diffHours < 1) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  }

  // Hours ago (same day)
  if (diffDays < 1 && messageDate.getDate() === now.getDate()) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear()) {
    return `Yesterday at ${messageDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }

  // Within the last week
  if (diffDays < 7) {
    const dayName = messageDate.toLocaleDateString([], { weekday: 'long' });
    return `${dayName} at ${messageDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }

  // Older than a week
  return messageDate.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

interface AICoachProps {
  events: SleepEvent[];
  child?: Child | null;
  goal?: SleepGoal;
  isSubscribed: boolean;
  onUpdateGoal?: (newGoal: Partial<SleepGoal>) => Promise<void>;
}

export function AICoach({ events, child, goal, isSubscribed, onUpdateGoal }: AICoachProps) {
  const {
    messages,
    input,
    setInput,
    isLoading,
    scrollAreaRef,
    lastMessageRef,
    handleSendMessage,
    handleRetryMessage,
    handleCancelResponse,
    clearConversation,
    handleSuggestedPrompt,
    handleGeneratePrompts,
    suggestedPrompts,
    isLoadingPrompts,
    isFreeTrialExhausted,
    freeTrialMessagesRemaining,
    isInitialized,
    handleUpdateGoals,
    handleToggleBookmark,
    bookmarkedMessages
  } = useChat({ events, child, goal, isSubscribed, onUpdateGoal });

  // Ref for the auto-growing textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset textarea height when input changes programmatically (e.g. after send)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Track which message's timestamp is currently visible (null = none)
  const [visibleTimestampIndex, setVisibleTimestampIndex] = useState<number | null>(null);

  // Handle message tap - single tap toggles timestamp/bookmark visibility
  const handleMessageTap = (index: number) => {
    setVisibleTimestampIndex(prev => prev === index ? null : index);
  };

  // Scroll to a specific message (used by bookmarks drawer)
  const scrollToMessage = (index: number) => {
    const messageElements = scrollAreaRef.current?.querySelectorAll('[data-message-index]');
    if (messageElements && messageElements[index]) {
      messageElements[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Briefly highlight the message
      setVisibleTimestampIndex(index);
    }
  };

  // Clear all bookmarks
  const handleClearAllBookmarks = () => {
    bookmarkedMessages.forEach(msg => {
      handleToggleBookmark(msg.originalIndex);
    });
  };

  // This block is no longer needed - we show the chat UI for all users
  // Free trial users see the chat with a badge, exhausted users see upgrade prompt

  return (
    <div className="flex flex-1 flex-col h-full">
      <Card className="flex flex-col h-full">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 flex-wrap">
                <span>AI Sleep Coach</span>
                {!isSubscribed && (
                  <Badge variant="secondary" className="text-xs font-normal bg-primary/10 text-primary border border-primary/20">
                    {isFreeTrialExhausted
                      ? "Trial ended"
                      : `${freeTrialMessagesRemaining} free ${freeTrialMessagesRemaining === 1 ? 'question' : 'questions'} left`}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Get personalized sleep advice and coaching for {child?.name}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <CoachTipsSheet />
              {!isFreeTrialExhausted && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear conversation?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will clear all messages and start a new conversation.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => clearConversation()}>
                        Clear
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0 relative">
          <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
            {!isInitialized ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    ref={index === messages.length - 1 ? lastMessageRef : null}
                    data-message-index={index}
                    className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                    onClick={() => handleMessageTap(index)}
                  >
                    {/* Message row with avatar and bubble */}
                    <div className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className={
                          message.role === 'model'
                            ? "bg-blue-100 text-blue-600"
                            : message.failed
                              ? "bg-destructive/10 text-destructive"
                              : "bg-green-100 text-green-600"
                        }>
                          {message.role === 'model' ? (
                            <Sparkles className="h-4 w-4" />
                          ) : message.failed ? (
                            <AlertTriangle className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className={`max-w-[80%] rounded-lg ${message.role === 'user'
                          ? message.failed
                            ? 'bg-destructive/5 border border-destructive/20'
                            : 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                          }`}
                      >
                        <div className="p-3">
                          {message.role === 'user' ? (
                            <p className={`text-sm whitespace-pre-wrap ${message.failed ? 'text-foreground' : ''}`}>
                              {message.content}
                            </p>
                          ) : (
                            <div className="text-sm">
                              <MarkdownRenderer content={message.content} />
                            </div>
                          )}
                        </div>
                        {message.failed && message.errorMessage && (
                          <div className="px-3 pb-3 pt-0 flex items-center gap-2 text-xs text-muted-foreground">
                            <AlertTriangle className="h-3 w-3 text-destructive flex-shrink-0" />
                            <span className="flex-1 text-destructive/80">{message.errorMessage}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetryMessage(index);
                              }}
                              disabled={isLoading}
                              className="h-6 px-2 text-xs hover:bg-destructive/10 hover:text-destructive"
                            >
                              <RotateCw className="h-3 w-3 mr-1" />
                              Retry
                            </Button>
                          </div>
                        )}
                        {message.suggestedGoals && (
                          <div className="px-3 pb-3 pt-0" onClick={(e) => e.stopPropagation()}>
                            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 shadow-sm mt-2">
                              <CardHeader className="p-3 pb-2">
                                <CardTitle className="text-xs font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-400">
                                  <Sparkles className="h-3.5 w-3.5" />
                                  Suggested Schedule Update
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="p-3 pt-1">
                                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs mb-3">
                                  <div className="text-muted-foreground">Bedtime:</div>
                                  <div className="font-medium text-foreground">{message.suggestedGoals.bedtime}</div>
                                  <div className="text-muted-foreground">Wake Up:</div>
                                  <div className="font-medium text-foreground">{message.suggestedGoals.wakeUpTime}</div>
                                  <div className="text-muted-foreground">Naps:</div>
                                  <div className="font-medium text-foreground">{message.suggestedGoals.numNaps} (max {message.suggestedGoals.maxAwakeWindow}h awake)</div>
                                </div>
                                <Button
                                  size="sm"
                                  className={`w-full h-7 text-xs ${message.applied ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white transition-colors`}
                                  onClick={() => handleUpdateGoals(message.suggestedGoals!, index)}
                                  disabled={message.applied || isLoading}
                                >
                                  {message.applied ? (
                                    <span className="flex items-center gap-1 justify-center">
                                      <Sparkles className="h-3 w-3" />
                                      Schedule Updated
                                    </span>
                                  ) : "Update Schedule"}
                                </Button>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Timestamp and bookmark row - always rendered, animated height */}
                    {message.timestamp && (
                      <div
                        className={`grid transition-all duration-200 ease-out ${visibleTimestampIndex === index || message.bookmarked
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'grid-rows-[0fr] opacity-0'
                          }`}
                      >
                        <div className="overflow-hidden">
                          <div
                            className={`flex items-center gap-2 mt-1 px-1 select-none ${message.role === 'user' ? 'mr-11 justify-end' : 'ml-11 justify-start'
                              }`}
                          >
                            {/* For AI messages: bookmark icon on left, timestamp on right */}
                            {message.role === 'model' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleBookmark(index);
                                }}
                                className="p-1 rounded-full hover:bg-muted/80 transition-colors"
                                aria-label={message.bookmarked ? 'Remove bookmark' : 'Bookmark this tip'}
                              >
                                <Bookmark
                                  className={`h-3.5 w-3.5 transition-colors ${message.bookmarked
                                    ? 'fill-primary text-primary'
                                    : 'text-muted-foreground hover:text-primary'
                                    }`}
                                />
                              </button>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {formatMessageTimestamp(message.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        <Sparkles className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggested Prompts */}
                {!isLoading && (
                  <div className="space-y-2">
                    {suggestedPrompts.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Sparkles className="h-4 w-4" />
                            <span>Suggestions:</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleGeneratePrompts}
                            disabled={isLoadingPrompts || isFreeTrialExhausted}
                            className="h-9 px-4 text-sm min-w-[110px] shrink-0"
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingPrompts ? 'animate-spin' : ''}`} />
                            Refresh
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {suggestedPrompts.map((prompt, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              onClick={() => handleSuggestedPrompt(prompt)}
                              disabled={isLoading || isFreeTrialExhausted}
                              className="text-xs h-auto py-2 px-3 whitespace-normal text-left"
                            >
                              {prompt}
                            </Button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleGeneratePrompts}
                          disabled={isLoadingPrompts || isFreeTrialExhausted}
                          className="h-8 text-xs text-muted-foreground hover:text-foreground border-dashed"
                        >
                          {isLoadingPrompts ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                              Generating suggestions...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3 mr-2 text-purple-500" />
                              Need inspiration? Let me suggest a starting point
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          <div className="p-4 border-t">
            {isFreeTrialExhausted ? (
              <div className="flex flex-col items-center gap-3 p-4 bg-gradient-to-br from-indigo-50/80 via-purple-50/40 to-blue-50/80 dark:from-indigo-950/40 dark:via-purple-950/30 dark:to-blue-950/40 border border-primary/20 rounded-xl shadow-xs text-center">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground flex items-center justify-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Unlock Unlimited AI Sleep Coaching
                  </p>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    {child?.name
                      ? `Get 24/7 personalized awake window schedules, night-waking solutions, and expert sleep guidance for ${child.name}.`
                      : "Get 24/7 personalized awake window schedules, night-waking solutions, and expert sleep guidance."}
                  </p>
                  <p className="text-xs font-semibold text-primary">
                    £4.99 / month • Cancel anytime
                  </p>
                </div>
                <UpgradePrompt message="Unlimited 24/7 Guidance" compact hideIcon />
              </div>
            ) : (
              <div className="flex gap-2 items-end">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask about sleep patterns, tips, or advice..."
                  disabled={isLoading}
                  rows={1}
                  className="flex-1 resize-none overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ maxHeight: '120px' }}
                />
                {isLoading ? (
                  <Button
                    onClick={handleCancelResponse}
                    variant="outline"
                    size="icon"
                    aria-label="Stop response"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSendMessage()}
                    disabled={!input.trim()}
                    size="icon"
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
                {/* Saved messages button - in input bar */}
                {bookmarkedMessages.length > 0 && (
                  <BookmarksDrawer
                    bookmarkedMessages={bookmarkedMessages}
                    onScrollToMessage={scrollToMessage}
                    onRemoveBookmark={handleToggleBookmark}
                    onClearAll={handleClearAllBookmarks}
                  />
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div >
  );
}
